import { RawFulfillmentGroup } from '../types';

export interface DeliveryTrackerRequestPayload {
  deliveryTrackerRequest: {
    customerId: string;
    fulfillmentType: string | null;
    orderNumber: string;
    trackingNumber: string | null;
    workOrderNumber: string | null;
  };
}

// Fulfillment types the real production experience app's own GetDeliveryTrackerInfo never calls
// the tracker for — pickup/in-store/unclassified orders have nothing a carrier could be tracking.
// "Delivery tracker" is scoped to the ship-to-home/BODFS/BOSS/Appliance case here (mirroring
// FulfillmentDeliveryTracker.tsx's DELIVERY_STEP_TITLES branch) — BOPIS/WillCall pickup tracking is
// a different UI concern (that component's other branch, gated on a `lineItems[].statusCode` field
// this integration doesn't request) and is out of scope.
const INELIGIBLE_FULFILLMENT_TYPES = new Set([
  'Additional Items',
  'BOPIS',
  'BuyOnlinePickupInStore',
  'WillCallFulfillment',
  'InStorePurchase',
  'MISC',
  'unavailable'
]);

// A line-item status that means there's nothing left to track — mirrors GetDeliveryTrackerInfo's
// exclusion list 1:1.
const INELIGIBLE_LINE_ITEM_STATUSES = new Set(['Canceled', 'Returned', 'Return Initiated', 'ReturnInProgress']);

/**
 * Whether this fulfillment group is worth a deliveryTracker call at all — mirrors the real
 * production experience app's own trigger condition (GetDeliveryTrackerInfo): a
 * trackingNumber/workOrderNumber to look up, a fulfillment type that's actually a carrier
 * delivery, and a line-item status that isn't already terminal. `trackingNumber`/`workOrderNumber`/
 * `lineItems` are the unconfirmed fields flagged in types.ts/orderDetailsDataModel.ts — this
 * returns `false` (not an error) whenever they're simply absent, so an environment where those
 * fields don't resolve just never calls the tracker rather than throwing.
 */
export function isDeliveryTrackerEligible(fulfillmentGroup: RawFulfillmentGroup | undefined): boolean {
  if (!fulfillmentGroup) {
    return false;
  }

  const hasIdentifier = Boolean(fulfillmentGroup.trackingNumber || fulfillmentGroup.workOrderNumber);
  const isEligibleFulfillmentType = !INELIGIBLE_FULFILLMENT_TYPES.has(fulfillmentGroup.fulfillmentType ?? '');
  const lineItemStatus = fulfillmentGroup.lineItems?.[0]?.statusDescription ?? '';
  const isEligibleLineItemStatus = !INELIGIBLE_LINE_ITEM_STATUSES.has(lineItemStatus);

  return hasIdentifier && isEligibleFulfillmentType && isEligibleLineItemStatus;
}

/**
 * Builds the deliveryTracker REST request body, mirroring the real production experience app's
 * IDeliveryTrackerRequest shape 1:1. Callers should check `isDeliveryTrackerEligible` first — this
 * doesn't re-derive it, to avoid two slightly different notions of "eligible" drifting apart.
 */
export function buildDeliveryTrackerRequest(
  fulfillmentGroup: RawFulfillmentGroup,
  orderNumber: string,
  customerAccountId: string
): DeliveryTrackerRequestPayload {
  return {
    deliveryTrackerRequest: {
      customerId: customerAccountId,
      fulfillmentType: fulfillmentGroup.fulfillmentType ?? null,
      orderNumber,
      trackingNumber: fulfillmentGroup.trackingNumber ?? null,
      workOrderNumber: fulfillmentGroup.workOrderNumber ?? null
    }
  };
}
